import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useId,
} from 'react';
import { useAuth } from 'lib/auth';
import { ROLES, SUB_ROLES } from 'lib/authorization';
import { PERMISSIONS } from 'lib/permissions';

/**
 * RoleProvider.jsx (authorization-integrated)
 * ------------------------------------------------------------------
 * - Uses your existing auth + authorization model
 * - Roles come from user.accountType (primary) and optional user.roles[]
 * - Permissions come from PERMISSIONS[role]
 * - SUPER approval requires ROLES.COUNTRY_ADMIN ("superAdmin")
 * - Exposes: useRoles(), useHasRole(), can(), RequireRole, RequireAnyRole
 * ------------------------------------------------------------------
 */

const RoleContext = createContext(null);

/** Normalize roles from the auth user into a flat, unique array of strings. */
function extractRoles(user) {
  if (!user) return [];
  const list = [];
  if (user.accountType) list.push(String(user.accountType));
  if (Array.isArray(user.roles)) list.push(...user.roles.map(String));
  if (user.subRole) list.push(String(user.subRole)); // optional companion to SUB_ROLES
  // de-dupe while preserving order
  return Array.from(new Set(list.filter(Boolean)));
}

/** Gather permissions for all roles the user currently holds. */
function resolvePermissions(roles) {
  const bag = new Set();
  for (const r of roles) {
    const p = PERMISSIONS[r] || [];
    for (const x of p) bag.add(x);
  }
  return bag; // Set<string>
}

export function RoleProvider({ children, validateApproval, roleConfig }) {
  const { user } = useAuth();
  const [approvalState, setApprovalState] = useState(null);

  const roles = useMemo(() => Object.freeze(extractRoles(user)), [user]);
  const allPerms = useMemo(() => resolvePermissions(roles), [roles]);

  const hasRole = useCallback((role) => roles.includes(role), [roles]);
  const hasAnyRole = useCallback(
    (some) => some.some((r) => roles.includes(r)),
    [roles]
  );

  const can = useCallback(
    (perm) => {
      // ROOT user shortcut
      if (roles.includes(ROLES.COUNTRY_ADMIN)) return true;
      return allPerms.has('*') || allPerms.has(perm);
    },
    [roles, allPerms]
  );

  /**
   * requireApproval: prompts for ROOT user (superAdmin) sign-off when caller isn't ROOT.
   */
  const requireApproval = useCallback(
    ({ reason }) => {
      if (roles.includes(ROLES.COUNTRY_ADMIN)) return Promise.resolve(true);
      return new Promise((resolve, reject) => {
        const id =
          globalThis.crypto && 'randomUUID' in globalThis.crypto
            ? globalThis.crypto.randomUUID()
            : String(Date.now());
        setApprovalState({ id, reason, resolve, reject });
      });
    },
    [roles]
  );

  const value = useMemo(
    () => ({
      user,
      roles,
      permissions: allPerms,
      hasRole,
      hasAnyRole,
      can,
      requireApproval,
    }),
    [user, roles, allPerms, hasRole, hasAnyRole, can, requireApproval]
  );

  return (
    <RoleContext.Provider value={value}>
      {children}
      <ApprovalDialog
        open={Boolean(approvalState)}
        reason={approvalState?.reason ?? ''}
        onCancel={() => {
          approvalState?.reject?.(new Error('Approval cancelled'));
          setApprovalState(null);
        }}
        onApprove={async (code) => {
          try {
            // If a ROOT user is currently logged in, they can approve with one click
            if (roles.includes(ROLES.COUNTRY_ADMIN)) {
              approvalState?.resolve?.(true);
              setApprovalState(null);
              return;
            }
            // Otherwise, delegate to the caller's validator (e.g., server check)
            const ok =
              (await validateApproval?.({
                reason: approvalState?.reason ?? '',
                code,
              })) ?? false;
            if (ok) {
              approvalState?.resolve?.(true);
              setApprovalState(null);
            } else {
              alert('Approval code invalid.');
            }
          } catch (e) {
            approvalState?.reject?.(e);
            setApprovalState(null);
          }
        }}
      />
    </RoleContext.Provider>
  );
}

/** Hooks */
export function useRoles() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRoles must be used within a <RoleProvider>');
  return ctx;
}
export const useHasRole = (role) => useRoles().hasRole(role);

/**
 * Require a single role. Uses your canonical role strings (e.g., ROLES.COUNTRY_ADMIN).
 */
export function RequireRole({ role, children, fallback = null }) {
  const has = useHasRole(role);
  return has ? children : fallback;
}

/**
 * Require any of the provided roles.
 */
export function RequireAnyRole({ roles, children, fallback = null }) {
  const { hasAnyRole } = useRoles();
  return hasAnyRole(roles) ? children : fallback;
}

/**
 * Minimal, glassmorphic approval dialog. Keep visual tokens neutral so
 * it blends with your shell (glassmorphism + dark-mode friendly).
 */
function ApprovalDialog({ open, reason, onApprove, onCancel }) {
  const [code, setCode] = useState('');
  const titleId = useId();
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 grid place-items-center"
    >
      <div
        className="absolute inset-0 backdrop-blur-md bg-black/40"
        onClick={onCancel}
      />
      <div className="relative w-[min(560px,92vw)] rounded-2xl p-6 shadow-2xl border border-white/20 bg-white/10 text-white dark:bg-neutral-900/60 dark:border-white/10">
        <h2 id={titleId} className="text-xl font-semibold mb-2">
          Super Admin approval required
        </h2>
        <p className="text-sm opacity-90 mb-4">
          Reason: <span className="font-medium">{reason}</span>
        </p>
        <div className="space-y-2 mb-4">
          <label className="block text-sm">
            Approval code from a ROOT user (superAdmin)
          </label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter one-time code"
            className="w-full rounded-xl px-3 py-2 outline-none bg-white/20 focus:bg-white/25 border border-white/30 dark:bg-white/10 dark:focus:bg-white/15"
          />
          <p className="text-xs opacity-70">
            Tip: If a ROOT user is signed in, they can just click Approve.
          </p>
        </div>
        <div className="flex items-center gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-3 py-2 rounded-xl border border-white/30 hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            onClick={() => onApprove?.(code)}
            className="px-4 py-2 rounded-xl font-medium shadow hover:shadow-lg border border-white/20 bg-white/30 backdrop-blur active:translate-y-[1px]"
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
